<template>
<div :style="themePageStyle">
  <div class="container" style="padding-top:24px;padding-bottom:100px;max-width:820px;">

    <!-- Header -->
    <div class="d-flex align-items-center mb-3">
      <button class="btn btn-sm btn-outline-secondary me-2" @click="$router.go(-1)">
        <i class="mdi mdi-arrow-left me-1"></i>Back
      </button>
      <h5 class="mb-0 fw-bold" :style="{color:darkMode?'#F5F0E8':'#1A1A1A'}">
        <i class="mdi mdi-file-document-edit me-2" style="color:#BFA164;"></i>Send Bill
      </h5>
    </div>

    <!-- Share panel post-send -->
    <div v-if="sentBill" class="cb-card cb-card-share">
      <h6 style="color:#50C878;font-weight:800;">
        <i class="mdi mdi-check-circle me-2"></i>Bill sent!
      </h6>
      <div style="font-size:13px;color:var(--fm-s,#6B6356);margin-bottom:12px;">
        {{ sentBill.billNumber }} · {{ form.currency }} {{ fNum(sentBill.totals.total) }} · valid {{ form.expiryDays }} days
      </div>
      <div class="cb-share-grid">
        <button class="btn btn-sm btn-outline-warning" @click="copyText(sentBill.publicUrl)">
          <i class="mdi mdi-content-copy me-1"></i>Copy link
        </button>
        <a class="btn btn-sm btn-success" :href="whatsappHref" target="_blank">
          <i class="mdi mdi-whatsapp me-1"></i>WhatsApp
        </a>
        <a class="btn btn-sm btn-outline-info" :href="smsHref">
          <i class="mdi mdi-message-text me-1"></i>SMS
        </a>
        <button class="btn btn-sm btn-outline-secondary" @click="openQr = true">
          <i class="mdi mdi-qrcode me-1"></i>QR code
        </button>
        <button class="btn btn-sm btn-outline-primary" @click="$router.push('/sell/bills')">
          <i class="mdi mdi-file-document-multiple me-1"></i>My bills
        </button>
      </div>
      <div style="font-size:11px;color:var(--fm-s,#6B6356);margin-top:10px;word-break:break-all;">
        {{ sentBill.publicUrl }}
      </div>

      <div v-if="openQr" class="cb-qr-modal-backdrop" @click.self="openQr = false">
        <div class="cb-qr-modal">
          <button class="cb-qr-close" @click="openQr = false"><i class="mdi mdi-close"></i></button>
          <div style="color:#6B6356;font-size:11px;text-transform:uppercase;letter-spacing:1px;font-weight:600;">Show this QR to your buyer</div>
          <img :src="qrLarge" alt="Bill QR" class="cb-qr-img" />
          <div style="font-size:13px;color:#6B6356;margin-top:8px;">They scan it → open the bill → tap Pay via M-Pesa.</div>
        </div>
      </div>
    </div>

    <!-- Create form -->
    <div v-else>
      <!-- Buyer picker -->
      <div class="cb-card">
        <h6 class="cb-section-title"><i class="mdi mdi-account me-2"></i>Who's paying?</h6>
        <div class="row g-2">
          <div class="col-md-6">
            <label class="cb-label">Buyer phone <span class="text-danger">*</span></label>
            <div style="position:relative;">
              <span style="position:absolute;left:12px;top:50%;transform:translateY(-50%);color:#8A827A;font-size:13px;">+254</span>
              <input v-model="form.buyerPhone" type="tel" class="cb-input" placeholder="7XX XXX XXX"
                     style="padding-left:52px;" @blur="lookupBuyer" />
            </div>
            <div v-if="buyerFound" style="font-size:11px;color:#50C878;margin-top:4px;">
              <i class="mdi mdi-check-circle me-1"></i>Existing Flockr user: {{ buyerFound.display_name }}
            </div>
            <div v-else-if="form.buyerPhone && checkedPhone" style="font-size:11px;color:#FFAB00;margin-top:4px;">
              <i class="mdi mdi-information-outline me-1"></i>Not on Flockr yet — they'll get an SMS invite with the bill.
            </div>
          </div>
          <div class="col-md-6">
            <label class="cb-label">Buyer name <span v-if="!buyerFound">(optional)</span></label>
            <input v-model="form.buyerName" type="text" class="cb-input" placeholder="e.g. Simon Selembu" />
          </div>
        </div>
      </div>

      <!-- Line items -->
      <div class="cb-card">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <h6 class="cb-section-title mb-0"><i class="mdi mdi-format-list-bulleted me-2"></i>Line items</h6>
          <button v-if="templates.length" class="btn btn-sm btn-outline-warning" @click="showTemplates = true">
            <i class="mdi mdi-content-copy me-1"></i>Load template
          </button>
        </div>
        <div v-for="(line, li) in form.lines" :key="'l-'+li" class="cb-line-row">
          <div class="row g-2">
            <div class="col-md-5">
              <input v-model="line.description" type="text" class="cb-input" placeholder="e.g. Front brake pad" />
            </div>
            <div class="col-md-2">
              <input v-model.number="line.quantity" type="number" step="0.1" min="0.1" class="cb-input" placeholder="Qty" @input="recalc" />
            </div>
            <div class="col-md-3">
              <input v-model.number="line.unit_price" type="number" step="0.01" min="0" class="cb-input" placeholder="Unit price" @input="recalc" />
            </div>
            <div class="col-md-2 text-end" style="font-weight:700;color:#BFA164;padding-top:10px;">
              {{ form.currency }} {{ fNum(line.quantity * line.unit_price) }}
              <button v-if="form.lines.length > 1" class="btn btn-sm btn-link text-danger p-0 ms-2" @click="removeLine(li)">
                <i class="mdi mdi-close"></i>
              </button>
            </div>
          </div>
        </div>
        <button class="btn btn-sm btn-outline-secondary mt-2" @click="addLine">
          <i class="mdi mdi-plus me-1"></i>Add line
        </button>
      </div>

      <!-- Add-ons -->
      <div class="cb-card">
        <h6 class="cb-section-title"><i class="mdi mdi-plus-box-outline me-2"></i>Add-ons</h6>
        <div class="row g-2">
          <div class="col-md-4">
            <label class="cb-label">Transport / shipping</label>
            <input v-model.number="form.transportFee" type="number" step="0.01" min="0" class="cb-input" placeholder="0" @input="recalc" />
          </div>
          <div class="col-md-4">
            <label class="cb-label">Discount</label>
            <input v-model.number="form.discount" type="number" step="0.01" min="0" class="cb-input" placeholder="0" @input="recalc" />
          </div>
          <div class="col-md-4">
            <label class="cb-label">Valid for (days)</label>
            <select v-model.number="form.expiryDays" class="cb-input">
              <option :value="3">3 days</option>
              <option :value="7">7 days</option>
              <option :value="14">14 days</option>
              <option :value="30">30 days</option>
            </select>
          </div>
        </div>
        <div class="mt-2">
          <label class="cb-label">Notes to buyer (optional)</label>
          <textarea v-model="form.notes" class="cb-input" rows="2" placeholder="Delivery timeline, warranty, warehouse pickup instructions..."></textarea>
        </div>
      </div>

      <!-- Totals preview -->
      <div class="cb-card cb-card-totals">
        <div class="cb-total-row"><span>Items subtotal</span><span>{{ form.currency }} {{ fNum(totals.subtotal) }}</span></div>
        <div v-if="totals.transport_fee > 0" class="cb-total-row"><span>Transport</span><span>{{ form.currency }} {{ fNum(totals.transport_fee) }}</span></div>
        <div v-if="totals.discount > 0" class="cb-total-row" style="color:#50C878;"><span>Discount</span><span>−{{ form.currency }} {{ fNum(totals.discount) }}</span></div>
        <div class="cb-total-row cb-total-grand"><span>Total buyer pays</span><span>{{ form.currency }} {{ fNum(totals.total) }}</span></div>
        <div class="cb-total-row" style="color:var(--fm-s,#6B6356);"><span>Platform fee ({{ totals.platform_fee_pct }}%) — deducted from your payout</span><span>−{{ form.currency }} {{ fNum(totals.platform_fee) }}</span></div>
        <div class="cb-total-row" style="color:#50C878;font-size:12px;"><span>You net (after platform fee)</span><span>{{ form.currency }} {{ fNum(totals.total - totals.platform_fee) }}</span></div>
      </div>

      <!-- Actions -->
      <div v-if="error" style="color:#FF6B6B;font-size:12px;margin-bottom:10px;">
        <i class="mdi mdi-alert me-1"></i>{{ error }}
      </div>
      <div class="d-flex gap-2 flex-wrap">
        <button class="btn btn-warning fw-bold flex-fill" @click="createBill(true)" :disabled="submitting || !canSubmit">
          <span v-if="submitting"><span class="spinner-border spinner-border-sm me-1"></span>Sending…</span>
          <span v-else><i class="mdi mdi-send me-1"></i>Send bill to buyer</span>
        </button>
        <button class="btn btn-outline-secondary" @click="createBill(false)" :disabled="submitting || !canSubmit">
          <i class="mdi mdi-content-save-outline me-1"></i>Save draft
        </button>
        <button class="btn btn-outline-warning" @click="saveAsTemplate" :disabled="!canSubmit">
          <i class="mdi mdi-bookmark-outline me-1"></i>Save as template
        </button>
      </div>
    </div>

    <!-- Templates modal -->
    <div v-if="showTemplates" class="cb-qr-modal-backdrop" @click.self="showTemplates = false">
      <div class="cb-qr-modal" style="text-align:left;">
        <button class="cb-qr-close" @click="showTemplates = false"><i class="mdi mdi-close"></i></button>
        <h6 style="font-weight:800;">Load a template</h6>
        <div v-for="t in templates" :key="t.id" class="cb-template-row" @click="applyTemplate(t)">
          <div>
            <div style="font-weight:600;">{{ t.name }}</div>
            <div style="font-size:11px;color:#6B6356;">{{ jsonLines(t.lines_json).length }} lines · updated {{ formatDate(t.updated_at) }}</div>
          </div>
          <i class="mdi mdi-chevron-right"></i>
        </div>
        <div v-if="!templates.length" style="text-align:center;color:#6B6356;padding:20px 0;">No templates yet.</div>
      </div>
    </div>

  </div>
</div>
</template>

<script>
/* eslint-disable */
import { execute, executeJson, flocker } from '@/api';

export default {
  name: 'CreateBill',
  data(){
    return {
      darkMode: (document.documentElement.getAttribute('data-theme') === 'dark'),
      form: {
        buyerPhone: '',
        buyerName: '',
        buyerId: '',
        currency: 'KES',
        transportFee: 0,
        discount: 0,
        expiryDays: 7,
        notes: '',
        lines: [{ description: '', quantity: 1, unit_price: 0 }]
      },
      totals: { subtotal: 0, transport_fee: 0, discount: 0, platform_fee: 0, platform_fee_pct: 3, total: 0 },
      buyerFound: null,
      checkedPhone: false,
      submitting: false,
      error: '',
      sentBill: null,
      openQr: false,
      templates: [],
      showTemplates: false
    };
  },
  computed: {
    themePageStyle(){return this.darkMode?'background:#0A0A0A;min-height:100vh;color:#F5F0E8;':'background:#F7F5F2;min-height:100vh;color:#1A1A1A;'},
    canSubmit(){
      var validLines = this.form.lines.filter(l => l.description && Number(l.quantity) > 0 && Number(l.unit_price) > 0).length;
      return this.form.buyerPhone && validLines > 0;
    },
    whatsappHref(){
      if (!this.sentBill) return '';
      var msg = 'Hi' + (this.form.buyerName ? ' ' + this.form.buyerName : '') + ', here\'s your bill from Flockr: '
             + this.form.currency + ' ' + this.fNum(this.sentBill.totals.total)
             + '. Pay securely via M-Pesa: ' + this.sentBill.publicUrl
             + '\n\nValid ' + this.form.expiryDays + ' days · Escrow-protected.';
      var phone = this.form.buyerPhone.replace(/\D/g, '');
      if (phone.length === 9) phone = '254' + phone;
      else if (phone.length === 10 && phone.charAt(0) === '0') phone = '254' + phone.substr(1);
      return 'https://wa.me/' + phone + '?text=' + encodeURIComponent(msg);
    },
    smsHref(){
      if (!this.sentBill) return '';
      var phone = this.form.buyerPhone.replace(/\D/g, '');
      if (phone.length === 9) phone = '0' + phone;
      else if (phone.length === 12 && phone.substr(0,3) === '254') phone = '0' + phone.substr(3);
      var msg = 'Flockr bill: ' + this.form.currency + ' ' + this.fNum(this.sentBill.totals.total) + ' — pay via M-Pesa here: ' + this.sentBill.publicUrl;
      return 'sms:' + phone + '?body=' + encodeURIComponent(msg);
    },
    qrLarge(){
      if (!this.sentBill) return '';
      return 'https://api.qrserver.com/v1/create-qr-code/?size=500x500&margin=12&data=' + encodeURIComponent(this.sentBill.publicUrl);
    }
  },
  mounted(){
    this.recalc();
    this.loadTemplates();
  },
  methods: {
    fNum(n){ return Number(n || 0).toLocaleString('en-KE', { maximumFractionDigits: 2 }); },
    addLine(){ this.form.lines.push({ description: '', quantity: 1, unit_price: 0 }); this.recalc(); },
    removeLine(i){ this.form.lines.splice(i, 1); this.recalc(); },
    recalc(){
      var sub = 0;
      this.form.lines.forEach(l => { sub += Number(l.quantity || 0) * Number(l.unit_price || 0); });
      var t = Number(this.form.transportFee || 0);
      var d = Number(this.form.discount || 0);
      var pct = 3;
      var base = Math.max(0, sub + t - d);
      var fee = Math.round(base * pct) / 100;
      // Issue 27: buyer pays the base amount; the fee comes out of the seller payout
      this.totals = { subtotal: Math.round(sub * 100) / 100, transport_fee: t, discount: d, platform_fee: fee, platform_fee_pct: pct, total: Math.round(base * 100) / 100 };
    },
    async lookupBuyer(){
      this.checkedPhone = true;
      this.buyerFound = null;
      if (!this.form.buyerPhone) return;
      try {
        var r = await executeJson({ function: 'lookupUserByPhone', phone: this.form.buyerPhone }, flocker);
        if (r.data && r.data.success && r.data.data && r.data.data.user) {
          this.buyerFound = r.data.data.user;
          this.form.buyerId = r.data.data.user.id;
          if (!this.form.buyerName) this.form.buyerName = r.data.data.user.display_name;
        }
      } catch(e){}
    },
    async createBill(sendNow){
      this.error = '';
      if (!this.canSubmit) { this.error = 'Add at least one complete line item with a quantity and price.'; return; }
      this.submitting = true;
      try {
        var params = {
          function: 'createBill',
          sellerId: sessionStorage.getItem('userId') || '',
          buyerPhone: this.form.buyerPhone,
          buyerName: this.form.buyerName,
          buyerId: this.form.buyerId,
          currency: this.form.currency,
          transportFee: this.form.transportFee,
          discount: this.form.discount,
          expiryDays: this.form.expiryDays,
          notes: this.form.notes,
          lines: JSON.stringify(this.form.lines.filter(l => l.description && Number(l.quantity) > 0 && Number(l.unit_price) > 0)),
          sendNow: sendNow ? 1 : 0
        };
        var r = await executeJson(params, flocker);
        if (r.data && r.data.success && r.data.data) {
          if (sendNow) this.sentBill = r.data.data;
          else this.$router.push('/sell/bills');
        } else {
          this.error = (r.data && r.data.message) || 'Failed to create bill';
        }
      } catch(e) { this.error = 'Network error'; }
      this.submitting = false;
    },
    async saveAsTemplate(){
      var name = prompt('Save this bill as a template. Give it a name:');
      if (!name) return;
      try {
        await executeJson({
          function: 'saveBillTemplate',
          sellerId: sessionStorage.getItem('userId') || '',
          name: name,
          lines: JSON.stringify(this.form.lines),
          transportFee: this.form.transportFee,
          discount: this.form.discount,
          currency: this.form.currency,
          notes: this.form.notes
        }, flocker);
        alert('Template saved.');
        this.loadTemplates();
      } catch(e) { alert('Failed to save template'); }
    },
    async loadTemplates(){
      try {
        var r = await executeJson({ function: 'listBillTemplates', sellerId: sessionStorage.getItem('userId') || '' }, flocker);
        if (r.data && r.data.success) this.templates = r.data.data.templates || [];
      } catch(e){}
    },
    applyTemplate(t){
      var lines = this.jsonLines(t.lines_json);
      this.form.lines = lines.length ? lines : this.form.lines;
      this.form.transportFee = Number(t.transport_fee || 0);
      this.form.discount = Number(t.discount || 0);
      this.form.currency = t.currency || 'KES';
      this.form.notes = t.notes || '';
      this.showTemplates = false;
      this.recalc();
    },
    jsonLines(str){
      try { var arr = typeof str === 'string' ? JSON.parse(str) : str; return Array.isArray(arr) ? arr : []; } catch(e) { return []; }
    },
    formatDate(d){ if (!d) return ''; return new Date(d).toLocaleDateString('en-KE', { day:'numeric', month:'short' }); },
    copyText(text){
      if (!text) return;
      try { navigator.clipboard.writeText(text); }
      catch(e){
        var ta = document.createElement('textarea'); ta.value = text; document.body.appendChild(ta); ta.select();
        try { document.execCommand('copy'); } catch(_){}
        document.body.removeChild(ta);
      }
    }
  }
};
</script>

<style scoped>
.cb-card { background:#FFFFFF; border:1px solid #E8E2DA; border-radius:12px; padding:18px; margin-bottom:14px; }
html[data-theme="dark"] .cb-card { background:#141414; border-color:#1E1E1E; color:#F5F0E8; }
.cb-card-share { border-color:#50C878; background:rgba(80,200,120,.05); }
.cb-card-totals { background:rgba(191,161,100,.06); border-color:#BFA164; }
.cb-section-title { font-size:14px; font-weight:800; color:#BFA164; text-transform:uppercase; letter-spacing:.5px; margin-bottom:12px; }
.cb-label { display:block; font-size:11px; color:#6B6356; font-weight:600; text-transform:uppercase; letter-spacing:.4px; margin-bottom:4px; }
.cb-input { width:100%; padding:9px 12px; border:1px solid #E0D8D0; border-radius:8px; font-size:13px; color:#1A1A1A; background:#FFFFFF; }
html[data-theme="dark"] .cb-input { background:#0F0F0F; border-color:#2A2520; color:#F5F0E8; }
.cb-input:focus { outline:none; border-color:#BFA164; }
.cb-line-row { padding:8px 0; border-bottom:1px dashed rgba(191,161,100,.2); }
.cb-line-row:last-of-type { border-bottom:0; }
.cb-total-row { display:flex; justify-content:space-between; padding:6px 0; font-size:13px; }
.cb-total-grand { border-top:1px solid #BFA164; font-weight:800; font-size:16px; padding-top:10px; margin-top:6px; color:#BFA164; }
.cb-share-grid { display:flex; flex-wrap:wrap; gap:8px; }
.cb-qr-modal-backdrop { position:fixed; inset:0; background:rgba(0,0,0,.6); display:flex; align-items:center; justify-content:center; z-index:9000; padding:16px; }
.cb-qr-modal { background:#FFFFFF; border-radius:14px; padding:24px; width:100%; max-width:440px; text-align:center; position:relative; color:#1A1A1A; }
.cb-qr-close { position:absolute; top:8px; right:10px; background:transparent; border:none; font-size:22px; color:#6B6356; cursor:pointer; }
.cb-qr-img { width:100%; max-width:360px; height:auto; border-radius:10px; padding:12px; border:1px solid #E8E2DA; margin-top:8px; }
.cb-template-row { display:flex; justify-content:space-between; align-items:center; padding:12px; border:1px solid #E8E2DA; border-radius:8px; margin-bottom:8px; cursor:pointer; }
.cb-template-row:hover { border-color:#BFA164; background:rgba(191,161,100,.05); }
</style>
